import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  Banknote,
  Boxes,
  Building2,
  CircleDot,
  CloudOff,
  FileText,
  FolderPlus,
  GitCompare,
  Info,
  Layers,
  List,
  ListChecks,
  Map as MapIcon,
  Package,
  PackageCheck,
  Plus,
  ReceiptText,
  RotateCw,
  SearchX,
  ShoppingCart,
  Tag,
  Truck,
  Unlink,
  UserRound,
  UserRoundPlus,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canEdit as canEditModule, hasPermission, SUPER_ADMIN_ROLE } from '@/lib/permissions';
import { formatDate, formatNumber } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { parseSortParam, formatSortParam } from '@/lib/table';
import { readStored, storageKey, writeStored } from '@/lib/storage';
import { useUrlParam, useUrlState } from '@/hooks/useUrlState';
import { useIsPhone } from '@/hooks/useMediaQuery';
import { Button } from '@/components/ui/button';
import { BulkBar } from '@/components/ui/bulk-bar';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterBar } from '@/components/ui/filter-bar';
import { notify } from '@/components/ui/notify';
import { PageHeader } from '@/components/ui/page-header';
import { Segmented } from '@/components/ui/segmented';
import { useConfirm } from '@/components/ui/confirm-dialog';
import ProjectFormPanel from '@/components/projects/ProjectFormPanel';
import { ProjectsMapView } from '@/components/projects/ProjectsMapView';
import { AddPinsPanel } from '@/components/projects/AddPinsPanel';
import { pinOf } from '@/components/projects/mapModel';
import BulkEditDialog from '@/components/projects/BulkEditDialog';
import { ProjectsTable } from '@/components/projects/ProjectsTable';
import { ProjectCards } from '@/components/projects/ProjectCards';
import { useListLayout } from '@/components/projects/useListLayout';
import {
  AttentionTiles,
  GroupToggle,
  ScopeToggle,
  SortMenu,
  statusTabId,
  StatusTabs,
} from '@/components/projects/ProjectsToolbar';
import {
  ATTENTION_OPTIONS,
  attentionCounts,
  awaitingApprovalCount,
  cleanText,
  countByStatus,
  countSites,
  deleteConfirmName,
  deleteImpactDescription,
  deleteImpactItems,
  deleteKeptItems,
  describeGroup,
  EMPTY_PROJECT_FORM,
  FILTER_KEYS,
  filterOptions,
  filterProjects,
  groupBySite,
  matchesStatusTab,
  NONE_VALUE,
  personKeys,
  plural,
  projectCity,
  projectHref,
  projectToDuplicateForm,
  projectToForm,
  siteBaseName,
  siteKeyOf,
  sortProjects,
  STATUS_TABS,
  visibleTags,
} from '@/components/projects/projectsModel';

const SCOPE_STORAGE_KEY = storageKey('projects', 'list', 'scope');
const LEGACY_SCOPE_KEY = 'projects.list.scope'; // wave 1 (a plain string)
const TABS_ID = 'projects-status';
const PANEL_ID = 'projects-status-panel';
const DELETE_UNDO_MS = 10000;
// Mirrors the server (DELETE /projects/{id}: Super Admin with the projects permission — owner decision).
const DELETE_ROLES = new Set([SUPER_ADMIN_ROLE]);
const formatCount = (count) => formatNumber(count, { maxDecimals: 0 });

const IMPACT_ICONS = {
  boq_items: List,
  lpos: ShoppingCart,
  invoices: ReceiptText,
  variation_invoices: ReceiptText,
  stock_movements: Package,
  reservations: PackageCheck,
  delivery_notes: Truck,
  site_requests: Truck,
  documents: FileText,
  variations: GitCompare,
  execution_tasks: ListChecks,
  structure: Building2,
  material_site_needs: Boxes,
  payment_terms: Banknote,
};

/*
  Everything a link can open the list with lives in the URL (03 #5): ?status=Active|On Hold|Completed|all,
  ?q=, ?mine=1, ?attention=late|awaiting|no-engineer|needs-setup, ?sort=attention|due:asc|…, ?grouped=0,
  and one repeated key per filter (?system=BMS&system=LCS, ?city=Abu Dhabi, ?engineer=none → no
  Project Engineer — the dashboard's wave 1 link). ?view=map opens the Map view (03 #10) with the same
  filters. Writes replace the history entry, so Back leaves the page.
*/
const statusFromParam = (value) => {
  const wanted = String(value || '').trim().toLowerCase().replace(/[-_+]+/g, ' ');
  if (!wanted) return undefined;
  return STATUS_TABS.find((tab) => tab.key.toLowerCase() === wanted || tab.label.toLowerCase() === wanted)?.key;
};
const STATUS_PARAM = { defaultValue: 'Active', parse: statusFromParam, serialize: (value) => value };
const VIEW_PARAM = {
  defaultValue: 'list',
  parse: (value) => (String(value || '').trim().toLowerCase() === 'map' ? 'map' : undefined),
  serialize: (value) => value,
};
const NO_FILTERS = Object.fromEntries(FILTER_KEYS.map((key) => [key, []]));
const URL_SCHEMA = {
  q: '',
  status: STATUS_PARAM,
  mine: false,
  attention: '',
  sort: 'attention',
  grouped: true,
  view: VIEW_PARAM,
  ...NO_FILTERS,
};

const countText = (item) => `${formatCount(item.count)} ${item.label}`;
const joinWords = (items) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

/** Under the delete list: what stays behind without a project, and other projects with the same name. */
function DeleteImpactNotes({ kept, sameNameCount, name }) {
  const noteClass = 'flex items-start gap-2 rounded-xl border border-line bg-surface-2 px-3 py-2 text-[12.5px] leading-snug text-ink-2';
  return (
    <>
      {kept.length ? (
        <p className={noteClass} data-testid="project-delete-kept">
          <Unlink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
          <span>
            <b className="font-semibold text-ink">Not deleted: </b>
            {kept.map(countText).join(' and ')}
            {kept.length === 1 && kept[0].count === 1 ? ' stays' : ' stay'}, but will refer to a project that no longer exists.
          </span>
        </p>
      ) : null}
      {sameNameCount ? (
        <p className={noteClass} data-testid="project-delete-same-name">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
          <span>
            {sameNameCount === 1 ? '1 other project is' : `${sameNameCount} other projects are`} also named “{name}” — only
            this one is deleted.
          </span>
        </p>
      ) : null}
    </>
  );
}

const uniqueSorted = (values) => {
  const seen = new Map();
  values.map(cleanText).filter(Boolean).forEach((value) => { if (!seen.has(value.toLowerCase())) seen.set(value.toLowerCase(), value); });
  return [...seen.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};

function copyText(text) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  return new Promise((resolve, reject) => {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    if (ok) resolve(); else reject(new Error('Copy is not available in this browser'));
  });
}

const BULK_DONE = {
  project_engineer: (value, count) => `Project Engineer ${value ? `set to ${value}` : 'cleared'} on ${plural(count, 'project')}`,
  senior_project_engineer: (value, count) => `Senior Project Engineer ${value ? `set to ${value}` : 'cleared'} on ${plural(count, 'project')}`,
  status: (value, count) => `${plural(count, 'project')} set to ${value === 'On Hold' ? 'On hold' : value}`,
  add_tags: (value, count) => `Tag “${value[0]}” added to ${plural(count, 'project')}`,
};

export default function ProjectsPage() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const isPhone = useIsPhone();
  // Edit level of Projects (Admin › Access, 28 #8): Viewer accounts and roles set to View only get the read-only
  // page — no New project, row edits, bulk changes or map pin actions (the server refuses those writes anyway).
  const canEdit = canEditModule(user, 'projects');
  const canDelete = DELETE_ROLES.has(cleanText(user?.role)) && hasPermission(user, 'projects');
  const myName = cleanText(user?.name);
  // "Mine" matches the name or the name part of the email, like the dashboard (ali.eyad@… = "Ali Eyad").
  const myKeys = useMemo(() => personKeys(user), [user]);

  const [projects, setProjects] = useState([]);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | error
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [platformUsers, setPlatformUsers] = useState([]);
  const [f, setF] = useUrlState(URL_SCHEMA);
  const query = useDeferredValue(f.q);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulk, setBulk] = useState(null); // { mode, projects, seq }
  const [pinsPanel, setPinsPanel] = useState({ open: false, ids: [], seq: 0 }); // "Add missing pins" (03 #10)
  const [panel, setPanel] = useState({ open: false, mode: 'create', project: null, form: EMPTY_PROJECT_FORM, seq: 0 });
  const projectsRequestSequenceRef = useRef(0);
  const listRef = useRef(null);
  // Table (wide / medium columns) or cards, from the width the list has — never a sideways-scrolling table.
  const layout = useListLayout(listRef, { isPhone });
  const showCards = layout === 'cards' || layout === 'cards-2';
  const now = useMemo(() => Date.now(), []);

  /* ---------- data ---------- */

  const fetchProjects = useCallback(async ({ signal } = {}) => {
    const requestSequence = ++projectsRequestSequenceRef.current;
    setRefreshing(true);
    try {
      // The whole list once (63 projects), with a light stage summary per project (03 #6); search and
      // filters then run on the loaded list, so they also find engineers, systems and tags.
      const response = await api.get('/projects', { params: { stage_summary: true }, signal });
      if (requestSequence !== projectsRequestSequenceRef.current) return;
      setProjects(Array.isArray(response.data) ? response.data : []);
      setLoadError('');
      setLoadState('ready');
    } catch (err) {
      if (err.code === 'ERR_CANCELED' || requestSequence !== projectsRequestSequenceRef.current) return;
      // Never turn a failed load into "No projects": keep the last good list and say what happened.
      setLoadError(getErrorMessage(err, 'The server did not answer.'));
      setLoadState((previous) => (previous === 'ready' ? 'ready' : 'error'));
    } finally {
      if (requestSequence === projectsRequestSequenceRef.current) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchProjects({ signal: controller.signal });
    return () => controller.abort();
  }, [fetchProjects]);

  useEffect(() => {
    const controller = new AbortController();
    api.get('/admin/users', { signal: controller.signal })
      .then((response) => setPlatformUsers(Array.isArray(response.data) ? response.data : []))
      .catch((err) => {
        if (err.code !== 'ERR_CANCELED') setPlatformUsers([]);
      });
    return () => controller.abort();
  }, []);

  // "Mine" is remembered in this browser (wave 1); a link with ?mine= always wins.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('mine') && readStored(SCOPE_STORAGE_KEY, readLegacyScope()) === 'mine' && myName) setF({ mine: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setScope = (scope) => {
    setF({ mine: scope === 'mine' });
    writeStored(SCOPE_STORAGE_KEY, scope);
  };

  /* ---------- the list ---------- */

  const mine = Boolean(f.mine && myName);
  const filters = useMemo(() => ({ ...f, q: query, mine }), [f, query, mine]);
  const inScope = useMemo(() => (mine ? filterProjects(projects, { mine: true }, { myName: myKeys }) : projects), [projects, mine, myKeys]);
  const scopedProjects = useMemo(() => filterProjects(projects, filters, { myName: myKeys, now }), [projects, filters, myKeys, now]);
  const statusCounts = useMemo(() => countByStatus(scopedProjects), [scopedProjects]);
  const visibleProjects = useMemo(
    () => scopedProjects.filter((project) => matchesStatusTab(project, f.status)),
    [scopedProjects, f.status]
  );
  const sort = f.sort === 'attention' ? null : parseSortParam(f.sort);
  const sortedProjects = useMemo(() => sortProjects(visibleProjects, sort || 'attention', { now }), [visibleProjects, sort?.key, sort?.dir, now]); // eslint-disable-line react-hooks/exhaustive-deps
  const entries = useMemo(() => (
    f.grouped
      ? groupBySite(sortedProjects).map((entry) => (entry.kind === 'group' ? { ...entry, info: describeGroup(entry, { now }) } : entry))
      : sortedProjects.map((project) => ({ kind: 'project', project }))
  ), [f.grouped, sortedProjects, now]);
  const groupEntries = useMemo(() => entries.filter((entry) => entry.kind === 'group'), [entries]);

  const tableRows = useMemo(() => {
    const rows = [];
    entries.forEach((entry) => {
      if (entry.kind === 'project') {
        rows.push({ key: entry.project.id, kind: 'project', project: entry.project });
        return;
      }
      const expanded = expandedGroups.has(entry.key);
      rows.push({ key: entry.key, kind: 'group', group: entry, info: entry.info, expanded });
      if (expanded) {
        entry.projects.forEach((project, index) => rows.push({
          key: project.id, kind: 'child', project, group: entry, last: index === entry.projects.length - 1,
        }));
      }
    });
    return rows;
  }, [entries, expandedGroups]);

  const toggleGroup = useCallback((key) => setExpandedGroups((previous) => {
    const next = new Set(previous);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  }), []);

  /* ---------- selection (03 #7): project ids; a group row stands for all its projects ---------- */

  const selectable = canEdit && !showCards;
  useEffect(() => {
    // The cards have no check boxes: switching to them (a narrower window) drops the selection.
    if (!selectable) setSelectedIds((previous) => (previous.length ? [] : previous));
  }, [selectable]);
  useEffect(() => {
    // Only projects still in the list stay selected (a filter never leaves hidden rows selected).
    const present = new Set(visibleProjects.map((project) => project.id));
    setSelectedIds((previous) => (previous.every((id) => present.has(id)) ? previous : previous.filter((id) => present.has(id))));
  }, [visibleProjects]);
  const selectedKeys = useMemo(() => {
    const ids = new Set(selectedIds);
    return [...selectedIds, ...groupEntries.filter((group) => group.projects.every((p) => ids.has(p.id))).map((group) => group.key)];
  }, [selectedIds, groupEntries]);
  const onSelectionChange = useCallback((nextKeys) => {
    const next = new Set(nextKeys);
    const previous = new Set(selectedKeys);
    const ids = new Set(selectedIds);
    tableRows.forEach((row) => {
      if (row.kind === 'group') return;
      if (next.has(row.key)) ids.add(row.key);
      else if (previous.has(row.key)) ids.delete(row.key);
    });
    groupEntries.forEach((group) => {
      const was = previous.has(group.key);
      const isSelected = next.has(group.key);
      if (isSelected && !was) group.projects.forEach((project) => ids.add(project.id));
      if (!isSelected && was) group.projects.forEach((project) => ids.delete(project.id));
    });
    setSelectedIds([...ids]);
  }, [selectedKeys, selectedIds, tableRows, groupEntries]);
  const selectGroup = useCallback((group) => {
    setSelectedIds((previous) => [...new Set([...previous, ...group.projects.map((project) => project.id)])]);
  }, []);
  const selectedProjects = useMemo(() => {
    const ids = new Set(selectedIds);
    return visibleProjects.filter((project) => ids.has(project.id));
  }, [selectedIds, visibleProjects]);
  const selectedSite = useMemo(() => {
    const names = [...new Set(selectedProjects.map((project) => siteKeyOf(project)))];
    const [first] = selectedProjects;
    return names.length === 1 && selectedProjects.length > 1 ? siteBaseName(first.name, first.system_type) : '';
  }, [selectedProjects]);

  /* ---------- options ---------- */

  const peopleNames = useMemo(() => uniqueSorted([
    ...platformUsers.map((entry) => entry.name),
    ...projects.flatMap((project) => [project.project_engineer, project.senior_project_engineer]),
  ]), [platformUsers, projects]);

  // Chip counts are for the status tab on screen ("BMS 30" = 30 rows on this tab), other filters aside.
  const tabProjects = useMemo(() => inScope.filter((project) => matchesStatusTab(project, f.status)), [inScope, f.status]);
  // "Needs: Awaiting approval · 16 stages": the header link counts stages, the list shows their projects (8),
  // so the chip repeats the stage count of the projects listed.
  const attentionOptions = useMemo(() => {
    if (f.attention !== 'awaiting') return ATTENTION_OPTIONS;
    const stages = visibleProjects.reduce((sum, project) => sum + awaitingApprovalCount(project), 0);
    return ATTENTION_OPTIONS.map((option) => (option.value === 'awaiting' && stages
      ? { ...option, label: `${option.label} · ${plural(stages, 'stage')}` }
      : option));
  }, [f.attention, visibleProjects]);
  const filterDefs = useMemo(() => {
    const options = (key, emptyLabel) => {
      const list = filterOptions(tabProjects, key, { emptyLabel });
      if (emptyLabel && (f[key] || []).includes(NONE_VALUE) && !list.some((option) => option.value === NONE_VALUE)) {
        list.push({ value: NONE_VALUE, label: emptyLabel, count: 0, muted: true });
      }
      return list;
    };
    const def = (key, label, extra = {}) => ({
      key, label, options: options(key, extra.emptyLabel), value: f[key], onChange: (value) => setF({ [key]: value }),
      testId: extra.testId || `projects-filter-${key}`, more: extra.more, popoverClassName: 'w-72',
    });
    return [
      def('system', 'System', { emptyLabel: 'No system type' }),
      def('engineer', 'Engineer', { emptyLabel: 'No engineer' }),
      def('city', 'City', { emptyLabel: 'No location', more: isPhone }),
      def('client', 'Client', { emptyLabel: 'No client', more: isPhone }),
      def('scope', 'Scope', { more: true }),
      def('tag', 'Tags', { more: true, testId: 'tag-filter' }),
      def('consultant', 'Consultant', { more: true }),
      def('contractor', 'Contractor', { more: true }),
      {
        key: 'attention',
        label: 'Needs',
        multiple: false,
        more: true,
        options: attentionOptions,
        value: f.attention || null,
        onChange: (value) => setF({ attention: value || '' }),
        testId: 'projects-filter-attention',
      },
    ];
  }, [f, tabProjects, isPhone, setF, attentionOptions]);
  const filtersActive = Boolean(f.q || f.attention || FILTER_KEYS.some((key) => (f[key] || []).length));
  const clearFilters = () => setF({ q: '', attention: '', ...NO_FILTERS });

  const formOptions = useMemo(() => {
    const clientCounts = {};
    projects.forEach((project) => {
      const key = cleanText(project.client).toLowerCase();
      if (key) clientCounts[key] = (clientCounts[key] || 0) + 1;
    });
    return {
      clients: uniqueSorted(projects.map((p) => p.client)),
      clientCounts,
      consultants: uniqueSorted(projects.map((p) => p.consultant)),
      contractors: uniqueSorted(projects.map((p) => p.contractor)),
      tags: uniqueSorted(projects.flatMap((p) => visibleTags(p.tags))),
      engineers: peopleNames,
    };
  }, [projects, peopleNames]);

  /* ---------- attention (03 #6) ---------- */

  const attention = useMemo(() => attentionCounts(inScope, { now }), [inScope, now]);
  const showAttention = (value) => {
    if (f.attention === value) setF({ attention: '' });
    else setF({ attention: value, status: 'Active' });
  };
  const alerts = [
    attention.late && { key: 'late', label: `${formatCount(attention.late)} past end date`, tone: 'red', onClick: () => showAttention('late') },
    attention.awaitingStages && {
      key: 'awaiting',
      // The link counts stages; the list it opens shows their projects ("16 stages on 8 projects").
      label: (
        <span title={`${plural(attention.awaitingStages, 'stage')} on ${plural(attention.awaitingProjects, 'project')} ${attention.awaitingStages === 1 ? 'is' : 'are'} awaiting approval`}>
          {plural(attention.awaitingStages, 'stage')} awaiting approval
        </span>
      ),
      tone: 'amber',
      onClick: () => showAttention('awaiting'),
    },
    attention.noEngineer && {
      key: 'no-engineer', label: `${formatCount(attention.noEngineer)} without a Project Engineer`, tone: 'amber', onClick: () => showAttention('no-engineer'),
    },
  ].filter(Boolean);
  const activeCount = useMemo(() => inScope.filter((project) => matchesStatusTab(project, 'Active')).length, [inScope]);
  const topCities = useMemo(() => {
    const counts = new Map();
    inScope.filter((project) => matchesStatusTab(project, 'Active')).forEach((project) => {
      const city = projectCity(project);
      if (city) counts.set(city, (counts.get(city) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 2).map(([city]) => city);
  }, [inScope]);

  /* ---------- create / edit / duplicate (03 #8) ---------- */

  const openPanel = useCallback((mode, project = null) => {
    setPanel((previous) => ({
      open: true,
      mode,
      project,
      form: mode === 'edit' ? projectToForm(project) : mode === 'duplicate' ? projectToDuplicateForm(project) : EMPTY_PROJECT_FORM,
      seq: previous.seq + 1,
    }));
  }, []);
  const openEdit = useCallback((project) => openPanel('edit', project), [openPanel]);
  // /projects?new=1 (the Dashboard's "New project") opens the New project panel once; the flag then leaves the
  // URL, so a reload or Back does not open it again.
  const [newParam, setNewParam] = useUrlParam('new', '');
  useEffect(() => {
    if (newParam !== '1') return;
    if (canEdit) openPanel('create');
    setNewParam('');
  }, [newParam, canEdit, openPanel, setNewParam]);
  const openDuplicate = useCallback((project) => openPanel('duplicate', project), [openPanel]);

  const submitProject = async (payload) => {
    if (panel.mode === 'edit' && panel.project?.id) {
      await api.put(`/projects/${panel.project.id}`, payload);
      notify.success(`Project “${payload.name}” updated`);
    } else {
      await api.post('/projects', payload);
      notify.success(`Project “${payload.name}” created`);
    }
    fetchProjects();
  };

  /* ---------- row actions ---------- */

  const copyProjectLink = useCallback(async (project) => {
    const url = `${window.location.origin}${projectHref(project)}`;
    try {
      await copyText(url);
      notify.info('Link copied', { description: url });
    } catch {
      notify.error('Could not copy the link', { description: url });
    }
  }, []);

  const setProjectOnHold = async (project) => {
    try {
      await api.put(`/projects/${project.id}`, { status: 'On Hold' });
      notify.success(`“${project.name}” is now On hold`);
      fetchProjects();
    } catch (err) {
      notify.error(`Could not put “${project.name}” on hold`, { description: getErrorMessage(err) });
    }
  };

  const handleDeleteProject = async (project) => {
    if (!canDelete) return;
    let impact;
    try {
      const response = await api.get(`/projects/${project.id}/delete-impact`);
      impact = response.data || {};
    } catch (err) {
      notify.error(`Could not check what “${project.name}” is linked to`, { description: getErrorMessage(err) });
      return;
    }

    // Collapsed spaces: the dialog displays the name that way, so that is what people can type.
    const name = deleteConfirmName(project.name);
    const removed = deleteImpactItems(impact);
    const kept = deleteKeptItems(impact);
    const details = impact.project || project;
    const createdAt = formatDate(details.created_at, { fallback: '' });
    const sameNameCount = Number(impact.same_name_projects) || 0;
    let undoId = '';

    const deleted = await confirm({
      title: `Delete “${name}”?`,
      subtitle: [cleanText(details.system_type), cleanText(details.status), createdAt && `created ${createdAt}`].filter(Boolean).join(' · '),
      description: deleteImpactDescription({ stages: Number(impact.stages) || 0, removed, kept }),
      consequences: removed.map((item) => ({
        key: item.key, icon: IMPACT_ICONS[item.key] || Layers, count: formatCount(item.count), label: item.label,
      })),
      children: kept.length || sameNameCount ? (
        <DeleteImpactNotes kept={kept} sameNameCount={sameNameCount} name={name} />
      ) : undefined,
      tone: 'danger',
      confirmLabel: 'Delete project',
      requireText: name,
      // Paused or finished work keeps its history with a status change instead of a delete.
      alternative: matchesStatusTab(project, 'On Hold') || matchesStatusTab(project, 'Completed') ? undefined : {
        text: 'Finished or paused? Set the status instead — it keeps the history.',
        label: 'Set On hold instead',
        onSelect: () => setProjectOnHold(project),
      },
      note: 'Only Super Admin can delete projects. You can undo right after deleting.',
      onConfirm: async () => {
        const response = await api.delete(`/projects/${project.id}`);
        undoId = cleanText(response.data?.undo_id);
      },
    });
    if (!deleted) return;

    setProjects((previous) => previous.filter((item) => item.id !== project.id));
    notify.success(`Project “${name}” deleted`, {
      duration: DELETE_UNDO_MS,
      description: removed.length
        ? `With ${removed.slice(0, 3).map(countText).join(', ')}${removed.length > 3 ? ', …' : ''}`
        : undefined,
      undo: undoId ? async () => {
        // Undo exactly this delete (a newer change by someone else is never rolled back).
        await api.post(`/undo/entries/${encodeURIComponent(undoId)}`);
        await fetchProjects();
      } : undefined,
      undoneMessage: `Project “${name}” restored`,
    });
    fetchProjects();
  };

  /* ---------- map pins (03 #10) ---------- */

  // The quick pin list covers the projects of this view (same filters and tab) that are not on the map.
  const unpinnedProjects = useMemo(() => visibleProjects.filter((project) => !pinOf(project)), [visibleProjects]);
  const openPins = useCallback((ids = []) => {
    setPinsPanel((previous) => ({ open: true, ids: Array.isArray(ids) ? ids : [], seq: previous.seq + 1 }));
  }, []);

  /* ---------- bulk changes (03 #7) ---------- */

  const openBulk = (mode, list = selectedProjects) => setBulk((previous) => ({ mode, projects: list, seq: (previous?.seq || 0) + 1 }));
  const applyBulk = async (body) => {
    const list = bulk?.projects || [];
    const response = await api.post('/projects/bulk-update', { ids: list.map((project) => project.id), ...body });
    const total = list.length;
    const reported = Number(response?.data?.updated);
    const updated = Number.isFinite(reported) ? reported : total;
    const [field, value] = Object.entries(body)[0];
    if (updated <= 0) {
      // Nothing was written (every project already had it), so there is nothing to undo either.
      notify.info(total === 1 ? 'Already set on this project — nothing changed' : `Already set on all ${total} projects — nothing changed`);
    } else {
      // A plain success toast: the shell turns it into "… · Undo" for this one history entry.
      const done = BULK_DONE[field] ? BULK_DONE[field](value, updated) : `${plural(updated, 'project')} updated`;
      notify.success(updated < total ? `${done} · ${total - updated} already had it` : done);
    }
    setBulk(null);
    setSelectedIds([]);
    fetchProjects();
  };

  /* ---------- render ---------- */

  const activeTabLabel = (STATUS_TABS.find((tab) => tab.key === f.status)?.label || '').toLowerCase();
  const hasQuery = Boolean(query) || filtersActive;
  const totalCount = loadState === 'ready' ? projects.length : undefined;
  const siteCount = useMemo(() => countSites(sortedProjects), [sortedProjects]);

  const renderEmpty = () => {
    if (!projects.length) {
      return (
        <EmptyState
          icon={FolderPlus}
          title="No projects yet"
          text="Create the first project — its 10 delivery stages are added from the default template."
          action={canEdit ? <Button type="button" onClick={() => openPanel('create')}><Plus aria-hidden="true" />New project</Button> : null}
        />
      );
    }
    if (mine && !hasQuery && statusCounts.all === 0) {
      return (
        <EmptyState
          icon={UserRound}
          title="No projects assigned to you"
          text="Projects where you are the Project Engineer or Senior Project Engineer show up here."
          action={<Button type="button" variant="outline" onClick={() => setScope('everyone')}>Show everyone’s projects</Button>}
        />
      );
    }
    const otherStatuses = f.status !== 'all' && statusCounts.all > 0;
    return (
      <EmptyState
        icon={SearchX}
        titleAs="p"
        title={f.status === 'all' ? 'No projects match' : `No ${activeTabLabel} projects match`}
        text={query
          ? `Nothing found for “${query}”. Search covers the name, client, place, engineers, system type, tags, scope and notes.`
          : 'Try another status, or remove a filter.'}
        action={(
          <div className="flex flex-wrap justify-center gap-2">
            {otherStatuses ? (
              <Button type="button" variant="outline" onClick={() => setF({ status: 'all' })} data-testid="projects-empty-all-statuses">
                Show all statuses ({statusCounts.all})
              </Button>
            ) : null}
            {mine ? (
              <Button type="button" variant="outline" onClick={() => setScope('everyone')}>Show everyone’s projects</Button>
            ) : null}
            {hasQuery ? (
              <Button type="button" variant="outline" onClick={clearFilters} data-testid="projects-empty-clear-filters">Clear filters</Button>
            ) : null}
          </div>
        )}
      />
    );
  };

  const summary = loadState === 'ready' ? (
    <span data-testid="projects-count" className="inline-flex items-center gap-1.5">
      {refreshing ? <RotateCw className="h-3 w-3 animate-spin text-ink-3" aria-label="Updating" /> : null}
      <span>
        <b>{formatCount(visibleProjects.length)}</b> {visibleProjects.length === 1 ? 'project' : 'projects'}
        {f.grouped && siteCount !== visibleProjects.length ? <> · <b>{formatCount(siteCount)}</b> {siteCount === 1 ? 'site' : 'sites'}</> : null}
      </span>
    </span>
  ) : null;

  const isMap = f.view === 'map';
  const listBody = (() => {
    if (loadState === 'error') {
      return (
        <EmptyState
          layout="inline"
          tone="red"
          icon={CloudOff}
          title="Couldn't load projects"
          text={`${loadError} Your projects are still there — this is a loading problem.`}
          action={<Button type="button" size="sm" variant="outline" onClick={() => fetchProjects()} data-testid="projects-retry"><RotateCw aria-hidden="true" />Retry</Button>}
          className="rounded-card border border-line bg-surface shadow-card"
          data-testid="projects-load-error"
        />
      );
    }
    if (isMap) {
      if (loadState === 'loading') {
        return (
          <div data-testid="projects-loading" className="ui-skeleton-shimmer h-[480px] rounded-card" aria-busy="true">
            <span className="sr-only" role="status">Loading...</span>
          </div>
        );
      }
      return (
        <ProjectsMapView
          projects={visibleProjects}
          others={f.status === 'all' ? [] : scopedProjects.filter((project) => !matchesStatusTab(project, f.status))}
          tabLabel={f.status === 'all' ? 'All statuses' : (STATUS_TABS.find((tab) => tab.key === f.status)?.label || f.status)}
          canEdit={canEdit}
          onAddPins={openPins}
          now={now}
          empty={renderEmpty()}
        />
      );
    }
    if (showCards) {
      if (loadState === 'loading') {
        return (
          <div data-testid="projects-loading" className={layout === 'cards-2' ? 'grid grid-cols-2 gap-2.5' : 'flex flex-col gap-2.5'} aria-busy="true">
            <span className="sr-only" role="status">Loading...</span>
            {Array.from({ length: layout === 'cards-2' ? 4 : 3 }, (_, index) => <div key={index} className="ui-skeleton-shimmer h-[150px] rounded-card" />)}
          </div>
        );
      }
      if (!entries.length) return <div className="rounded-card border border-line bg-surface shadow-card">{renderEmpty()}</div>;
      return (
        <ProjectCards
          columns={layout === 'cards-2' ? 2 : 1}
          entries={entries.map((entry) => (entry.kind === 'group' ? { kind: 'group', group: entry, info: entry.info } : entry))}
          expandedGroups={expandedGroups}
          onToggleGroup={toggleGroup}
          canEdit={canEdit}
          canDelete={canDelete}
          onEdit={openEdit}
          onDuplicate={openDuplicate}
          onDelete={handleDeleteProject}
          onCopyLink={copyProjectLink}
          onAssignMany={(list) => openBulk('engineer', list)}
          now={now}
        />
      );
    }
    return (
      <div data-testid={loadState === 'loading' ? 'projects-loading' : undefined}>
        {loadState === 'loading' ? <span className="sr-only" role="status">Loading...</span> : null}
        <ProjectsTable
          density={layout === 'medium' ? 'medium' : 'wide'}
          rows={tableRows}
          loading={loadState === 'loading'}
          empty={renderEmpty()}
          sort={sort}
          onSortChange={(next) => setF({ sort: formatSortParam(next) || 'attention' })}
          selectable={selectable}
          selectedKeys={selectedKeys}
          onSelectionChange={onSelectionChange}
          onToggleGroup={toggleGroup}
          onSelectGroup={selectGroup}
          canEdit={canEdit}
          canDelete={canDelete}
          onEdit={openEdit}
          onDuplicate={openDuplicate}
          onDelete={handleDeleteProject}
          onCopyLink={copyProjectLink}
          now={now}
          pagingKey={`${f.status}|${f.grouped}|${f.sort}`}
          className={refreshing && loadState === 'ready' ? 'opacity-80 transition-opacity' : 'transition-opacity'}
          renderBulkBar={({ returnFocus }) => (
            <BulkBar
              count={selectedProjects.length}
              noun={selectedSite ? `selected · ${selectedSite}` : 'selected'}
              onClear={() => setSelectedIds([])}
              returnFocus={returnFocus}
              testId="projects-bulkbar"
              actions={[
                { label: 'Assign Project Engineer', icon: UserRoundPlus, onSelect: () => openBulk('engineer'), testId: 'projects-bulk-engineer' },
                { label: 'Set status', icon: CircleDot, onSelect: () => openBulk('status'), testId: 'projects-bulk-status' },
                { label: 'Add tag', icon: Tag, onSelect: () => openBulk('tag'), testId: 'projects-bulk-tag' },
              ]}
            />
          )}
        />
      </div>
    );
  })();

  const attentionTiles = [
    { value: 'late', count: attention.late, short: 'past end date', tone: 'red' },
    // Stages, not projects: the list it opens says "8 projects".
    { value: 'awaiting', count: attention.awaitingStages, short: attention.awaitingStages === 1 ? 'stage awaiting' : 'stages awaiting', tone: 'amber' },
    { value: 'no-engineer', count: attention.noEngineer, short: 'no engineer', tone: 'amber' },
  ];

  // List | Map (03 #10): the same filtered projects as a table or on the map; ?view=map.
  const viewLabel = (Icon, text) => (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      <span className={isPhone ? 'sr-only' : undefined}>{text}</span>
    </span>
  );
  const viewSwitch = (
    <Segmented
      ariaLabel="Show the projects as"
      value={isMap ? 'map' : 'list'}
      onChange={(view) => setF({ view })}
      options={[
        { key: 'list', label: viewLabel(List, 'List'), title: 'Show the projects as a list', testId: 'projects-view-list' },
        { key: 'map', label: viewLabel(MapIcon, 'Map'), title: 'Show the projects on a map, by city', testId: 'open-projects-map' },
      ]}
      // Phones: two 44 px icon buttons (touch size, like the old map button).
      className={isPhone ? 'ml-auto [&>button]:h-11 [&>button]:min-w-11 [&>button]:justify-center' : undefined}
      testId="projects-view-switch"
    />
  );

  return (
    <div data-testid="projects-page" className="space-y-4 max-sm:pb-20">
      <PageHeader
        title="Projects"
        count={totalCount}
        subtitle={loadState === 'ready'
          ? `${plural(activeCount, 'active project')}${mine ? ' of yours' : ''}${topCities.length ? ` across ${joinWords(topCities)}` : ''}`
          : undefined}
        alerts={isPhone ? undefined : alerts}
        // Phones: List | Map (icons) at the end of the title row; New project is the floating button.
        titleAdornment={isPhone ? viewSwitch : null}
        actions={isPhone ? null : (
          <>
            {viewSwitch}
            {canEdit ? (
              <Button type="button" onClick={() => openPanel('create')} data-testid="create-project-btn">
                <Plus aria-hidden="true" />
                New project
              </Button>
            ) : null}
          </>
        )}
      />

      {/* Status tabs + Everyone | Mine */}
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 border-b border-line">
        <StatusTabs value={f.status} counts={statusCounts} onChange={(status) => setF({ status })} idPrefix={TABS_ID} panelId={PANEL_ID} />
        <div className="mb-2 flex items-center gap-3">
          {!isPhone && summary ? <span className="whitespace-nowrap text-[12.5px] text-ink-3 [&_b]:font-semibold [&_b]:text-ink-2" aria-live="polite">{summary}</span> : null}
          <ScopeToggle value={mine ? 'mine' : 'everyone'} onChange={setScope} disabled={!myName} />
        </div>
      </div>

      {/* Search + filter chips (in the URL) + count, grouping and order */}
      <FilterBar
        testId="projects-filters"
        // 03 mobile mockup: one row of chips that scrolls sideways under the search.
        phoneScroll
        search={{
          value: f.q,
          onChange: (q) => setF({ q }),
          placeholder: 'Search name, client, engineer, tag…',
          label: 'Search projects',
          testId: 'search-projects',
          // 260px: search, the four chips, More filters, Group by site and Sort share one row at 1440px
          // (a placeholder that does not fit ends in "…").
          className: 'sm:w-[260px] [&_input]:text-ellipsis',
        }}
        filters={filterDefs}
        onClearAll={clearFilters}
        clearable={filtersActive}
        actions={isPhone || isMap ? undefined : (
          <>
            <GroupToggle checked={f.grouped} onChange={(grouped) => setF({ grouped })} />
            <SortMenu value={f.sort} onChange={(next) => setF({ sort: next })} />
          </>
        )}
      />

      {isPhone ? (
        <>
          <AttentionTiles items={attentionTiles} value={f.attention} onChange={(value) => (value ? showAttention(value) : setF({ attention: '' }))} />
          {isMap ? null : (
            <div className="flex items-center justify-between gap-2 text-[12px] font-medium text-ink-3">
              <span>{summary}{loadState === 'ready' && f.sort === 'attention' ? ' · Needs attention first' : ''}</span>
              <SortMenu value={f.sort} onChange={(next) => setF({ sort: next })} compact />
            </div>
          )}
        </>
      ) : null}

      {loadError && loadState === 'ready' ? (
        <EmptyState
          layout="inline"
          tone="red"
          icon={CloudOff}
          title="Couldn't refresh the projects"
          text={`${loadError} The list below is from the last successful load.`}
          action={<Button type="button" size="sm" variant="outline" onClick={() => fetchProjects()}><RotateCw aria-hidden="true" />Retry</Button>}
          className="rounded-card border border-line bg-surface"
        />
      ) : null}

      {/* List */}
      <section
        ref={listRef}
        id={PANEL_ID}
        role="tabpanel"
        aria-labelledby={statusTabId(TABS_ID, f.status)}
        aria-busy={refreshing}
        className="min-w-0"
      >
        {listBody}
      </section>

      {canEdit && isPhone ? (
        <Button
          type="button"
          onClick={() => openPanel('create')}
          className="fixed bottom-[calc(16px+var(--app-bottom-nav-height,0px))] right-4 z-30 h-[52px] rounded-2xl pl-4 pr-[18px] text-[14px] font-bold shadow-overlay"
          data-testid="create-project-btn"
        >
          <Plus className="h-5 w-5" aria-hidden="true" />
          New project
        </Button>
      ) : null}

      {panel.seq ? (
        <ProjectFormPanel
          key={panel.seq}
          open={panel.open}
          onOpenChange={(open) => { if (!open) setPanel((previous) => ({ ...previous, open: false })); }}
          mode={panel.mode}
          sourceName={panel.project?.name || ''}
          projectId={panel.mode === 'edit' ? panel.project?.id : null}
          initialForm={panel.form}
          onSubmit={submitProject}
          projects={projects}
          options={formOptions}
        />
      ) : null}
      {bulk ? (
        <BulkEditDialog
          key={bulk.seq}
          mode={bulk.mode}
          projects={bulk.projects}
          people={peopleNames}
          tags={formOptions.tags}
          onApply={applyBulk}
          onClose={() => setBulk(null)}
        />
      ) : null}
      {pinsPanel.seq && canEdit ? (
        <AddPinsPanel
          key={pinsPanel.seq}
          open={pinsPanel.open}
          onOpenChange={(open) => { if (!open) setPinsPanel((previous) => ({ ...previous, open: false })); }}
          projects={unpinnedProjects}
          allProjects={projects}
          focusIds={pinsPanel.ids}
          onSaved={() => fetchProjects()}
        />
      ) : null}
    </div>
  );
}

/** Wave 1 stored the Everyone / Mine choice as a plain string under this key. */
function readLegacyScope() {
  try {
    return window.localStorage.getItem(LEGACY_SCOPE_KEY) === 'mine' ? 'mine' : 'everyone';
  } catch {
    return 'everyone';
  }
}
